// SleepDemo.jsx
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoon, faNoteSticky, faChartColumn, faListCheck } from '@fortawesome/free-solid-svg-icons';
import {
  TrackingBarColumn,
  TrackingBarLabel,
  TrackingBarsGrid,
  TrackingBarTrack,
  TrackingOverviewCard,
  TrackingOverviewFooter,
  TrackingOverviewFooterLabel,
  TrackingOverviewFooterValue,
  TrackingOverviewTitle,
  TrackingPeriodTab,
  TrackingPeriodTabs,
  TrackingSessionActions,
  TrackingSessionCard,
  TrackingSessionHeader,
  TrackingSessionList,
  TrackingSessionMetaItem,
  TrackingSessionMetaRow,
  TrackingSessionScore,
  TrackingSessionScoreLabel,
  TrackingSessionScoreValue,
  TrackingSessionsCard,
  TrackingSessionsTitle,
  TrackingSessionTitle,
} from '../../common/TrackingShared';
import {
  colors,
  EntryPanel,
  EntryActions,
  EntryPanelHeader,
  EntryPanelTitle,
  EntryPanelHint,
  Grid,
  Field,
  Label,
  Input,
  EntryDatePicker,
  OverviewHeader,
  OverviewMeta,
  OverviewMetaItem,
  OverviewBarValue,
  OverviewBarFill,
  NoteEditorForm,
  NoteFormGroup,
  NoteLabel,
  NoteLabelIcon,
  NoteLabelText,
  NoteTextarea,
  NoteCharCount,
  SleepTrackingCard,
  SleepTrackingHeader,
  SleepTrackingTitle,
  SleepTrackingStatus,
  StatusBadge,
  SleepTrackingActions,
  PrimaryActionButton,
  SleepTrackingHint,
  SaveButton,
  SessionActionButton,
  SessionMain,
  SessionRatingRow,
  SessionRatingDot,
  SessionNote,
  SessionNoteLabel,
  SessionNoteText,
} from '../../sleep/SleepJournal.styled';
import useProgressiveEntry from './useProgressiveEntry';

const DEMO_DISABLED_OPACITY = 0.6;

// ---- Mise en page englobante ----
const FullWidthSection = styled.div`
  width: 100%;
  margin-bottom: 3rem;

  button:disabled {
    opacity: ${DEMO_DISABLED_OPACITY} !important;
  }
`;

/* ─── Bloc alterné texte / visuel ─── */
const FeatureRow = styled.div`
  display: grid;
  grid-template-columns: ${({ $reverse }) => ($reverse ? '1fr minmax(240px, 340px)' : 'minmax(240px, 340px) 1fr')};
  align-items: center;
  gap: 2.5rem;
  padding: 2.25rem 0;

  &:not(:last-child) {
    border-bottom: 1px solid #eef1f4;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 1.5rem 0;
  }
`;

const FeatureRowText = styled.div`
  order: ${({ $reverse }) => ($reverse ? 2 : 1)};

  h3 {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-family: 'Poppins', sans-serif;
    font-size: 1.05rem;
    font-weight: 700;
    color: #1a2e40;
    margin: 0 0 0.6rem 0;
    svg { color: #2c5282; font-size: 0.95rem; }
  }
  p {
    font-family: 'Roboto', sans-serif;
    font-size: 0.9rem;
    color: #4a5568;
    line-height: 1.65;
    margin: 0 0 0.75rem 0;
    &:last-child { margin-bottom: 0; }
  }

  @media (max-width: 900px) {
    order: 1;
  }
`;

const FeatureRowVisual = styled.div`
  order: ${({ $reverse }) => ($reverse ? 1 : 2)};
  min-width: 0;

  @media (max-width: 900px) {
    order: 2;
  }
`;

// ---- Helpers ----
const formatHours = (value) => {
  if (!Number.isFinite(value)) return '—';
  const hours = Math.floor(value);
  const minutes = Math.round((value - hours) * 60);
  return `${hours}h${String(minutes).padStart(2, '0')}`;
};

const formatSignedHours = (value) => {
  if (!Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${formatHours(Math.abs(value))}`;
};

const computeSleepScore = (entryHours, targetHours) => {
  if (!Number.isFinite(entryHours) || entryHours <= 0 || !Number.isFinite(targetHours) || targetHours <= 0) {
    return { value: null, color: 'var(--bleu-mineral)' };
  }
  const ratio = Math.min(entryHours / targetHours, 1.2);
  const score = Math.round(Math.min(ratio * 100, 100));
  if (score >= 90) return { value: score, color: 'var(--vert-vitalite)' };
  if (score >= 75) return { value: score, color: '#8BC34A' };
  if (score >= 60) return { value: score, color: 'var(--jaune-energie)' };
  if (score >= 40) return { value: score, color: '#FF9800' };
  return { value: score, color: 'var(--rouge-alerte)' };
};

const scoreToDots = (score) => {
  if (!Number.isFinite(score)) return 0;
  return Math.max(0, Math.min(5, Math.round(score / 20)));
};

const formatSessionTitle = (isoDate) => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  });
};

// ---- Données mock ----
const TARGET_HOURS = 8;
const DEMO_DATE_KEY = '2026-08-06';
const DEMO_NOTE_VALUE = "Endormissement rapide, réveil unique vers 3h puis nuit calme jusqu'au matin.";

const MOCK_WEEK = [
  { id: 'w1', label: 'ven', at: '2026-07-31T23:00:00', value: 7.2 },
  { id: 'w2', label: 'sam', at: '2026-08-01T23:10:00', value: 7.4 },
  { id: 'w3', label: 'dim', at: '2026-08-02T22:50:00', value: 7.5 },
  { id: 'w4', label: 'lun', at: '2026-08-03T23:05:00', value: 7.7 },
  { id: 'w5', label: 'mar', at: '2026-08-04T22:40:00', value: 7.8 },
  { id: 'w6', label: 'mer', at: '2026-08-05T23:15:00', value: 7.0 },
  { id: 'w7', label: 'jeu', at: '2026-08-06T23:00:00', value: 7.1 },
];
const weekMax = Math.max(1, ...MOCK_WEEK.map((d) => d.value));
const weekAverage = MOCK_WEEK.reduce((s, d) => s + d.value, 0) / MOCK_WEEK.length;

const MOCK_SESSIONS = [
  { id: 's1', at: '2026-08-06T23:00:00', hours: 7.17, notes: '' },
  { id: 's2', at: '2026-08-05T23:15:00', hours: 7.0, notes: 'Réveil naturel, bonne nuit.' },
  { id: 's3', at: '2026-08-04T22:40:00', hours: 7.33, notes: '' },
];

// ---- Composant ----
const SleepDemo = () => {
  const [period, setPeriod] = useState('week');
  const bedtimeRef = useRef(null);
  const wakeTimeRef = useRef(null);
  const sleepNoteRef = useRef(null);
  const bedtime = useProgressiveEntry('23:00', { interval: 120, cycle: 8000, targetRef: bedtimeRef });
  const wakeTime = useProgressiveEntry('06:10', { delay: 350, interval: 120, cycle: 8000, targetRef: wakeTimeRef });
  const sleepNote = useProgressiveEntry(DEMO_NOTE_VALUE, { delay: 700, interval: 48, cycle: 8000, targetRef: sleepNoteRef });

  return (
    <FullWidthSection>

      {/* Bloc 1 — Suivi en temps réel */}
      <FeatureRow>
        <FeatureRowText>
          <h3><FontAwesomeIcon icon={faMoon} /> Un simple geste au coucher</h3>
          <p>
            Appuyez sur « Je me couche » et le suivi démarre automatiquement.
            L'heure de début est enregistrée précisément, sans que vous
            ayez à y penser.
          </p>
          <p>
            Le statut en temps réel vous rappelle si une session est en
            cours ou non.
          </p>
        </FeatureRowText>
        <FeatureRowVisual className="demo-preview-visual">
          <SleepTrackingCard className="landing-sleep-live-entry">
            <SleepTrackingHeader>
              <SleepTrackingTitle>Suivi de sommeil en temps réel</SleepTrackingTitle>
              <SleepTrackingStatus>
                <StatusBadge $active={false}>Inactif</StatusBadge>
              </SleepTrackingStatus>
            </SleepTrackingHeader>

            <SleepTrackingActions>
              <PrimaryActionButton>
                <FontAwesomeIcon icon={faMoon} /> Je me couche
              </PrimaryActionButton>
            </SleepTrackingActions>

            <SleepTrackingHint>
              Appuyez sur « Je me couche » pour démarrer automatiquement le suivi
              et enregistrer précisément votre heure de coucher.
            </SleepTrackingHint>
          </SleepTrackingCard>
        </FeatureRowVisual>
      </FeatureRow>

      {/* Bloc 2 — Saisie manuelle + note */}
      <FeatureRow $reverse>
        <FeatureRowText $reverse>
          <h3><FontAwesomeIcon icon={faNoteSticky} /> Racontez votre nuit</h3>
          <p>
            Vous pouvez aussi saisir une session manuellement : heure de
            coucher, heure de réveil, et une note libre pour décrire votre
            ressenti — réveils nocturnes, stress, rêves marquants...
          </p>
          <p>
            Jusqu'à 500 caractères, avec un compteur toujours visible pour
            ne jamais être pris de court.
          </p>
        </FeatureRowText>
        <FeatureRowVisual className="demo-preview-visual" $reverse>
          <EntryPanel>
            <EntryPanelHeader>
              <EntryPanelTitle>Nouvelle session de sommeil</EntryPanelTitle>
              <EntryPanelHint>
                Saisissez vos heures de coucher et de réveil pour enregistrer
                votre session.
              </EntryPanelHint>
            </EntryPanelHeader>

            <Grid>
              <Field>
                <Label>Date</Label>
                <EntryDatePicker
                  value={DEMO_DATE_KEY}
                  onChange={() => { }}
                  maxDateKey={DEMO_DATE_KEY}
                  ariaLabel="Date de l'entrée sommeil"
                  dialogLabel="Calendrier entrée sommeil"
                  showQuickActions
                />
              </Field>
              <Field>
                <Label>Heure de coucher</Label>
                <Input ref={bedtimeRef} type="text" inputMode="numeric" value={bedtime} readOnly aria-label="Heure de coucher" />
              </Field>
              <Field>
                <Label>Heure de réveil</Label>
                <Input ref={wakeTimeRef} type="text" inputMode="numeric" value={wakeTime} readOnly aria-label="Heure de réveil" />
              </Field>
            </Grid>

            <NoteEditorForm onSubmit={(e) => e.preventDefault()}>
              <NoteFormGroup>
                <NoteLabel htmlFor="sleep-demo-note">
                  <NoteLabelIcon icon={faNoteSticky} />
                  <NoteLabelText>Note pour cette nuit</NoteLabelText>
                </NoteLabel>
                <NoteTextarea
                  ref={sleepNoteRef}
                  id="sleep-demo-note"
                  value={sleepNote}
                  placeholder="Ajoutez une note personnelle (ex: réveils nocturnes, stress, ressenti...)"
                  rows={5}
                  maxLength={500}
                  readOnly
                />
                <NoteCharCount $isNearLimit={sleepNote.length >= 450}>
                  {sleepNote.length}/500 caractères
                </NoteCharCount>
              </NoteFormGroup>
            </NoteEditorForm>

            <EntryActions>
              <SaveButton type="button">
                Enregistrer
              </SaveButton>
            </EntryActions>
          </EntryPanel>
        </FeatureRowVisual>
      </FeatureRow>

      {/* Bloc 3 — Vue hebdomadaire */}
      <FeatureRow>
        <FeatureRowText>
          <h3><FontAwesomeIcon icon={faChartColumn} /> Une semaine en un coup d'œil</h3>
          <p>
            Les barres affichent la durée de chaque nuit, avec le pic de
            la semaine et le nombre d'entrées mis en évidence.
          </p>
          <p>
            Basculez entre semaine, mois et année pour repérer vos
            tendances de sommeil sur la durée.
          </p>
        </FeatureRowText>
        <FeatureRowVisual className="demo-preview-visual">
          <TrackingPeriodTabs>
            {[
              { key: 'week', label: 'Semaine' },
              { key: 'month', label: 'Mois' },
              { key: 'year', label: 'Année' },
            ].map((item) => (
              <TrackingPeriodTab
                key={item.key}
                type="button"
                $active={period === item.key}
                onClick={() => setPeriod(item.key)}
              >
                {item.label}
              </TrackingPeriodTab>
            ))}
          </TrackingPeriodTabs>

          <TrackingOverviewCard>
            <OverviewHeader>
              <TrackingOverviewTitle>Vue hebdomadaire</TrackingOverviewTitle>
              <OverviewMeta>
                <OverviewMetaItem>{MOCK_WEEK.length} entrée(s)</OverviewMetaItem>
                <OverviewMetaItem>Pic {formatHours(weekMax)}</OverviewMetaItem>
              </OverviewMeta>
            </OverviewHeader>

            <TrackingBarsGrid>
              {MOCK_WEEK.map((d, index) => (
                <TrackingBarColumn key={d.id} data-tooltip={`${d.label} · ${formatHours(d.value)}`} tabIndex={-1}>
                  <OverviewBarValue>{formatHours(d.value)}</OverviewBarValue>
                  <TrackingBarTrack>
                    <OverviewBarFill
                      className="sleep-demo-history-bar"
                      style={{ animationDelay: `${index * 500}ms` }}
                      $height={`${Math.round((d.value / weekMax) * 100)}%`}
                    />
                  </TrackingBarTrack>
                  <TrackingBarLabel>{d.label}</TrackingBarLabel>
                </TrackingBarColumn>
              ))}
            </TrackingBarsGrid>

            <TrackingOverviewFooter>
              <TrackingOverviewFooterLabel>Moyenne sommeil</TrackingOverviewFooterLabel>
              <TrackingOverviewFooterValue>{formatHours(weekAverage)}</TrackingOverviewFooterValue>
            </TrackingOverviewFooter>
          </TrackingOverviewCard>
        </FeatureRowVisual>
      </FeatureRow>

      {/* Bloc 4 — Historique des sessions */}
      <FeatureRow $reverse>
        <FeatureRowText $reverse>
          <h3><FontAwesomeIcon icon={faListCheck} /> Chaque nuit racontée</h3>
          <p>
            Chaque session affiche un score de qualité, l'écart avec votre
            objectif de sommeil et vos notes associées.
          </p>
          <p>
            Modifiez ou supprimez une entrée directement depuis
            l'historique, sans avoir à ressaisir toute la nuit.
          </p>
        </FeatureRowText>
        <FeatureRowVisual className="demo-preview-visual" $reverse>
          <TrackingSessionsCard>
            <TrackingSessionsTitle>Sessions sommeil</TrackingSessionsTitle>
            <TrackingSessionList>
              {MOCK_SESSIONS.map((entry) => {
                const score = computeSleepScore(entry.hours, TARGET_HOURS);
                const dots = scoreToDots(score.value);
                const delta = entry.hours - TARGET_HOURS;

                return (
                    <TrackingSessionCard key={entry.id} className="landing-sleep-session-entry" $borderColor={colors.border}>
                    <TrackingSessionHeader>
                      <SessionMain>
                        <TrackingSessionTitle>{formatSessionTitle(entry.at)}</TrackingSessionTitle>
                        <SessionRatingRow>
                          {[0, 1, 2, 3, 4].map((dotIndex) => (
                            <SessionRatingDot key={dotIndex} $active={dotIndex < dots} />
                          ))}
                        </SessionRatingRow>
                        <TrackingSessionMetaRow>
                          <TrackingSessionMetaItem>Sommeil: {formatHours(entry.hours)}</TrackingSessionMetaItem>
                          <TrackingSessionMetaItem>Cible: {formatHours(TARGET_HOURS)}</TrackingSessionMetaItem>
                          <TrackingSessionMetaItem>Écart: {formatSignedHours(delta)}</TrackingSessionMetaItem>
                        </TrackingSessionMetaRow>
                        {entry.notes ? (
                          <SessionNote>
                            <SessionNoteLabel>
                              <FontAwesomeIcon icon={faNoteSticky} />
                              Note
                            </SessionNoteLabel>
                            <SessionNoteText>{entry.notes}</SessionNoteText>
                          </SessionNote>
                        ) : null}
                      </SessionMain>
                      <TrackingSessionScore>
                        <TrackingSessionScoreValue $color={score.color}>
                          {score.value !== null ? `${score.value}%` : '—'}
                        </TrackingSessionScoreValue>
                        <TrackingSessionScoreLabel>Score</TrackingSessionScoreLabel>
                      </TrackingSessionScore>
                    </TrackingSessionHeader>

                    <TrackingSessionActions>
                      <SessionActionButton $variant="danger" type="button">Supprimer</SessionActionButton>
                      <SessionActionButton type="button">Modifier</SessionActionButton>
                    </TrackingSessionActions>
                  </TrackingSessionCard>
                );
              })}
            </TrackingSessionList>
          </TrackingSessionsCard>
        </FeatureRowVisual>
      </FeatureRow>
    </FullWidthSection>
  );
};

export default SleepDemo;
